/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { Table, TableBody, TableCell, TableContainer, TableContainerProps, TableHead, TableRow, Typography } from '@mui/material'
import { formatDate, formatDateTime } from '@om1/platform-components'
import { ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { defaultLabel } from '../pages/GenericDataPage'

interface DataItem {
    [key: string]: any
}

type NestedKeyOf<T> = {
    [K in keyof T]: T[K] extends object ? `${K & string}.${keyof T[K] & string}` : never
}[keyof T]

type ExtractTopLevelKeys<T> = T extends `${infer K}.${string}` ? K : never

export type ValidColumn<T> = keyof T | NestedKeyOf<T>

export const GenericDetailTable = <T extends DataItem>({
    data,
    columns,
    additionalColumns,
    type,
    idResolver,
    iconResolver,
    iconTitle,
    stickyHeader,
    ...props
}: {
    data: { data: T[] } | null
    columns: ValidColumn<T>[]
    additionalColumns?: {
        label: string
        value: (item: T) => ReactNode
    }[]
    type?: ExtractTopLevelKeys<ValidColumn<T>>
    idResolver?: (item: T) => string
    iconResolver?: (item: T) => JSX.Element
    iconTitle?: string
    stickyHeader?: boolean
} & Pick<TableContainerProps, 'sx' | 'className'>) => {
    const history = useHistory()

    const handleRowClick = (item: T) => {
        if (type) {
            const itemId = idResolver ? idResolver(item) : item[type]?.id
            if (itemId) {
                history.push(`/${type}s/${itemId}`)
            }
        }
    }

    const getColumnValue = (item: T, column: string) => {
        if (String(column).includes('Dttm')) {
            return formatDate(getNestedValue(item, String(column)) as string)
        }

        // Format changesNextStartingTimestamp with date and time
        if (String(column) === 'changesNextStartingTimestamp') {
            const value = getNestedValue(item, String(column)) as string
            if (!value) return 'N/A'
            return formatDateTime(value)
        }

        const value = getNestedValue(item, String(column))

        // If the value is null, undefined, or an empty string, return 'N/A'. Preserves other falsy values like 0.
        if (value === null || value === undefined || value === '') {
            return 'N/A'
        }

        return value
    }

    return (
        <TableContainer {...props} className={`overflow-y-auto w-[75dvw] pb-6 ${props.className}`}>
            {data?.data?.length && data?.data?.length > 0 ? (
                <Table stickyHeader={stickyHeader}>
                    <TableHead>
                        <TableRow>
                            {iconTitle && iconResolver && (
                                <TableCell className='border-b border-gray-200 text-sm w-[160px]'>
                                    <Typography className='text-sm' fontWeight='bold'>
                                        {iconTitle}
                                    </Typography>
                                </TableCell>
                            )}
                            {columns.map((column) => (
                                <TableCell key={String(column)} className='border-b border-gray-200 text-sm'>
                                    <Typography className='text-sm' fontWeight='bold'>
                                        {defaultLabel(String(column))}
                                    </Typography>
                                </TableCell>
                            ))}
                            {additionalColumns?.map((column) => (
                                <TableCell key={column.label} className='border-b border-gray-200 text-sm'>
                                    <Typography className='text-sm' fontWeight='bold'>
                                        {column.label}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.data.map((item, index) => (
                            <TableRow
                                key={index}
                                hover
                                className={type ? 'hover:bg-gray-100 cursor-pointer' : ''}
                                onClick={() => {
                                    if (type) {
                                        handleRowClick(item)
                                    }
                                }}
                            >
                                {iconResolver && <TableCell className='border-b border-gray-200 text-sm'>{iconResolver(item)}</TableCell>}
                                {columns.map((column) => (
                                    <TableCell key={String(column)}>{getColumnValue(item, String(column))}</TableCell>
                                ))}
                                {additionalColumns?.map((column) => (
                                    <TableCell key={column.label}>{column.value(item)}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <div className='flex justify-center items-center h-full'>
                    <Typography variant='h6'>No data found</Typography>
                </div>
            )}
        </TableContainer>
    )
}

export const getNestedValue = <T extends DataItem>(item: T, column: string) => {
    const keys = column.split('.')
    let value: any = item

    for (const key of keys) {
        if (value && typeof value === 'object') {
            value = value[key]
        } else {
            return ''
        }
    }

    return value ?? ''
}
